import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { BuyerClass } from '@app/certificates/packs/invoice-form/invoice-form';
import { InfoService } from '@app/core/info/info.service';
import { ICountryCode } from '@app/core/nomenclature/nomenclature';
import { NomenclatureService } from '@app/core/nomenclature/nomenclature.service';
import { ICompanyDataAsPayer, IUserData } from '@app/profile/main/details';
import { ITranslation } from '@app/shared/interfaces/common-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { PackService } from '../pack.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'cloud-invoice-form',
  templateUrl: './invoice-form.component.html',
  styleUrls: ['./invoice-form.component.scss']
})
export class InvoiceFormComponent implements OnInit {

  @ViewChild('invoiceForm') form: NgForm;
  @Input() userData: IUserData;
  @Input() countryCodes: ICountryCode[];

  checkedEmail = false;
  unsubscribe$: Subject<void> = new Subject<void>();
  lang: string;
  buyer;

  selectedClientType: string = 'individual';
  clientTypeClassificationNomenclature = [];
  statisticGroupNomenclature = [];
  companyData: ICompanyDataAsPayer;
  companyCountry: ITranslation;
  companyType: any;
  companyStatisticGroup: any;
  countries: ICountryCode[] = [];
  selectedCountry;
  companyIdValue;
  isLoadedCompanyData = false;
  submitted = false;
  showPopupNotFoundPayer = false;
  isSearchingCompany: boolean = false;
  isDisableCreateOrderButton: boolean = false;

  readonly PURCHASE_CQES_PACKAGES_ESTORE = environment.urlEstorePurchaseCqesPackages;

  isRestrictedPayer = false;
  readonly restrictedCompanyId = '201230426';
  readonly restrictedCountryCode = 'BG';

  legalEntities = [
    {
      name: 'packsTexts.individual',
      value: false,
      idTypes: [
        { name: 'idTypes.egn', value: 'E', code: 'EGN' },
        { name: 'idTypes.foreign', value: 'F', code: 'FOREIGN' }
      ]
    }
  ];

  clientTypes = [
    { label: 'individual', value: 'individual' },
    { label: 'legal', value: 'legal' }
  ];

  constructor(
    public info: InfoService,
    private translateService: TranslateService,
    private nomenclatureService: NomenclatureService,
    private packService: PackService
  ) {
    this.buyer = new BuyerClass();

    this.buyer.name = '';
    this.buyer.middleName = '';
    this.buyer.familyName = '';
    this.buyer.country = '';
    this.buyer.idType = '';
    this.buyer.idNumber = '';
    this.buyer.address = '';
    this.buyer.city = '';
    this.buyer.postalCode = '';
    this.buyer.isLegal = '';

    this.lang = this.translateService.currentLang;
  }

  ngOnInit() {
    this.info.dismissAll();
    this.setDefault();
    this.translateService.onLangChange.subscribe(langEvent => {
      this.setDefault();
      this.lang = langEvent.lang;
    });

    this.loadCountries();
  }

  private setDefault() {
    this.buyer.name = this.translate(this.userData.firstName);
    this.buyer.middleName = this.translate(this.userData.middleName);
    this.buyer.familyName = this.translate(this.userData.lastName);
    this.buyer.country = this.translate(this.userData.country);
    this.buyer.idType = this.mapIdType();
    this.buyer.idNumber = this.userData.personalId;
    this.buyer.address = this.translate(this.userData.address);
    this.buyer.city = this.translate(this.userData.settlement);
    this.buyer.postalCode = this.userData.postcode;
    this.buyer.isLegal = false;
  }

  loadCountries(): void {
    this.nomenclatureService.get().subscribe({
      next: (response) => {
        const countryCodes = response?.data?.countryCodes;
        if (countryCodes?.length) {
          this.countries = countryCodes;
          this.setDefaultCountry();
        }
      },
      error: (err) => {
        this.info.error(err);
      }
    });
  }

  getCountryName(country: ICountryCode): string {
    return country.countryName[this.lang] || country.countryName.bg;
  }

  private setDefaultCountry(): void {
    const defaultCountry = this.countries.find(c => c.code === 'BG');
    if (defaultCountry) {
      this.selectedCountry = defaultCountry;
    }
  }

  mapIdType() {
    if (this.userData.personalIdType === 'EGN') {
      return this.translateService.instant('idTypes.egn');
    } else if (this.userData.personalIdType === 'LNC') {
      return this.translateService.instant('idTypes.foreign');
    }
  }

  getErrorMessage(errorType, errors) {
    if (errors && errors.required) {
      return this.translateService.instant('validationTexts.requiredField');
    } else {
      return this.translateService.instant('validationTexts.' + errorType);
    }
  }

  translate(object?: ITranslation) {
    if (object) {
      return object[this.translateService.currentLang];
    } else {
      return '';
    }
  }

  isSelectedClientIndividual(): boolean {
    return this.selectedClientType === 'individual';
  }

  showCompanyData(): boolean {
    return this.selectedClientType !== 'individual' && this.isLoadedCompanyData;
  }

  showCompanyIdValueSection(): boolean {
    return this.selectedClientType !== 'individual' && !this.isLoadedCompanyData;
  }

  onClientTypeClick(clientType) {
    this.isDisableCreateOrderButton = clientType.value === 'legal' && !this.isLoadedCompanyData ? true : false;
  }

  onCheckCompanyIdValue(companyIdValueModel: NgModel) {
    this.submitted = true;
    companyIdValueModel.control.markAsTouched();
    this.getCompanyAsPayerData();
  }

  clearCompanyData() {
    this.isLoadedCompanyData = !this.isLoadedCompanyData;
    this.companyIdValue = '';
    this.submitted = false;
    this.isDisableCreateOrderButton = true;
  }

  getCompanyAsPayerData() {
    this.isSearchingCompany = true;
    this.packService.getCompanyAsPayerData(this.companyIdValue, this.selectedCountry.code)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (result) => {
        if (result.status === 200) {
          this.companyData = result.data.companyData;
          this.companyCountry = this.countries.find(c => c.longCode === this.companyData.companyCountryCode).countryName;
          this.clientTypeClassificationNomenclature = result.data.companyNomenclatures.clientTypeClassificationNomenclature;
          this.companyType = this.clientTypeClassificationNomenclature.find(c => c.number === this.companyData.clientTypeClassificationCode).description;
          this.statisticGroupNomenclature = result.data.companyNomenclatures.statisticGroupNomenclature;
          this.companyStatisticGroup = this.statisticGroupNomenclature.find(c => c.number === this.companyData.clientStatisticGroupCode).type;

          this.isLoadedCompanyData = !this.isLoadedCompanyData;
          this.isDisableCreateOrderButton = false;
        } else if(result.status === 404) {
          this.showPopupNotFoundPayer = true;
        } else {
          this.info.error(result.message);
        }
        this.isSearchingCompany = false;
      }
    );
  }

  checkRestrictedPayer(): void {
  this.isRestrictedPayer =
    this.companyIdValue === this.restrictedCompanyId &&
    this.selectedCountry?.code === this.restrictedCountryCode;
  }

}
